import type { ReactNode } from 'react';
import { juntarClasses } from './classes';
import { IconeAlerta, IconeCheck, IconeFechar, IconeInformacao } from './icones';

export type TomDeAviso = 'informacao' | 'atencao' | 'erro' | 'positivo';

interface EstiloDeAviso {
  moldura: string;
  icone: string;
}

const TONS: Readonly<Record<TomDeAviso, EstiloDeAviso>> = {
  informacao: { moldura: 'border-borda bg-superficie-vidro/60', icone: 'text-acento-indigo' },
  atencao: { moldura: 'border-atencao/40 bg-atencao/10', icone: 'text-atencao' },
  erro: { moldura: 'border-erro/40 bg-erro/10', icone: 'text-erro' },
  positivo: { moldura: 'border-economia/40 bg-economia/10', icone: 'text-economia' },
};

function iconePadrao(tom: TomDeAviso): JSX.Element {
  if (tom === 'positivo') return <IconeCheck className="h-5 w-5" />;
  if (tom === 'informacao') return <IconeInformacao className="h-5 w-5" />;
  return <IconeAlerta className="h-5 w-5" />;
}

export interface PropsAviso {
  children: ReactNode;
  tom?: TomDeAviso;
  titulo?: string;
  /** Substitui o ícone padrão do tom. */
  icone?: ReactNode;
  /** Ação à direita — "Reenviar", "Ver detalhes". */
  acao?: ReactNode;
  /** Presente ⇒ mostra o botão de dispensar. */
  aoFechar?: () => void;
  className?: string;
}

/**
 * Mensagem de estado no fluxo da tela.
 *
 * `role="alert"` só no tom de erro: `alert` interrompe o leitor de tela no meio
 * da frase, e usar isso para um aviso informativo transforma acessibilidade em
 * incômodo. Os demais tons usam `status`, que espera a pausa.
 *
 * O texto fica em `conteudo-primario` e a cor semântica vive na moldura e no
 * ícone — mesma razão do `Selo`: âmbar e verde não alcançam contraste de texto
 * no tema claro, mas passam como elemento gráfico.
 */
export function Aviso({
  children,
  tom = 'informacao',
  titulo,
  icone,
  acao,
  aoFechar,
  className,
}: PropsAviso): JSX.Element {
  const estilo = TONS[tom];

  return (
    <div
      role={tom === 'erro' ? 'alert' : 'status'}
      className={juntarClasses(
        'flex items-start gap-3 rounded-card border px-4 py-3',
        estilo.moldura,
        className,
      )}
    >
      <span className={juntarClasses('mt-0.5 shrink-0', estilo.icone)}>
        {icone ?? iconePadrao(tom)}
      </span>

      <div className="min-w-0 flex-1 text-sm text-conteudo-primario">
        {titulo !== undefined && <p className="font-medium">{titulo}</p>}
        <div className={juntarClasses('leading-relaxed', titulo !== undefined && 'mt-1')}>
          {children}
        </div>
      </div>

      {acao !== undefined && <div className="shrink-0">{acao}</div>}

      {aoFechar !== undefined && (
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Dispensar aviso"
          className="-mr-1 -mt-1 shrink-0 rounded-card p-1.5 text-conteudo-secundario transition-colors hover:bg-superficie-vidro hover:text-conteudo-primario"
        >
          <IconeFechar />
        </button>
      )}
    </div>
  );
}
